import React, { createContext, useContext, useState, useEffect } from 'react';
import authService from '../services/authService';

// Create the AuthContext
const AuthContext = createContext();

// Custom hook to use the AuthContext
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

// AuthProvider component
export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);

  const resetAuthState = () => {
    setUser(null);
    setIsAuthenticated(false);
  };

  // Initialize auth state on app load
  useEffect(() => {
    const initAuth = async () => {
      setLoading(true);
      try {
        // Check if we have tokens in either session or local storage.
        const accessToken = authService.getAccessToken();
        const storedUser = authService.getCurrentUser();
        
        if (accessToken && storedUser) {
          // Validate token with backend
          const result = await authService.validateToken();
          if (result.valid) {
            setUser(result.user);
            setIsAuthenticated(true);
          } else {
            // Token is invalid, logout
            authService.logout();
            resetAuthState();
          }
        }
      } catch (error) {
        // Log error internally without console output
        // If there's an error, ensure we're logged out
        authService.logout();
        resetAuthState();
      } finally {
        setLoading(false);
      }
    };

    initAuth();
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return undefined;
    }

    const handleLogout = () => {
      resetAuthState();
    };

    window.addEventListener('auth:logout', handleLogout);
    return () => window.removeEventListener('auth:logout', handleLogout);
  }, []);

  // Login function
  const login = async (username, password, rememberMe = false) => {
    try {
      const result = await authService.login(username, password, rememberMe);
      if (result.success) {
        setUser(result.user);
        setIsAuthenticated(true);
        return { success: true };
      } else {
        return { success: false, message: result.message };
      }
    } catch (error) {
      return { success: false, message: 'An unexpected error occurred' };
    }
  };

  // Register function
  const register = async (username, email, password, rememberMe = false) => {
    try {
      const result = await authService.register(username, email, password, rememberMe);
      if (result.success) {
        setUser(result.user);
        setIsAuthenticated(true);
        return { success: true };
      } else {
        return { success: false, message: result.message };
      }
    } catch (error) {
      return { success: false, message: 'An unexpected error occurred' };
    }
  };

  // Logout function
  const logout = () => {
    authService.logout();
    resetAuthState();
  };

  // Refresh token function
  const refreshToken = async () => {
    try {
      const result = await authService.refreshToken();
      return result;
    } catch (error) {
      // If refresh fails, logout user
      logout();
      return { success: false, message: 'Session expired. Please login again.' };
    }
  };

  // Context value
  const value = {
    user,
    isAuthenticated,
    loading,
    login,
    register,
    logout,
    refreshToken
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
